import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import { Radar, Plus, Upload, Search, ArrowRight, Sparkles, Mail, Loader2, Eye, MousePointerClick, AlertOctagon } from "lucide-react";

import { TIER_PILL, STATUS_LABEL } from "./opportunity/constants";
import { Kpi } from "./opportunity/Kpi";
import { FilterPills } from "./opportunity/FilterPills";
import { ProspectDialog } from "./opportunity/ProspectDialog";
import { CsvImportDialog } from "./opportunity/CsvImportDialog";
import { ProspectDetailDialog } from "./opportunity/ProspectDetailDialog";

export default function OpportunityEngine() {
  const [catalog, setCatalog] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ intent: "", clinic_type: "", state: "", city: "", status: "", q: "" });
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const pollRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [cat, dash, list] = await Promise.all([
        catalog ? Promise.resolve({ data: catalog }) : api.get("/opportunity/catalog"),
        api.get("/opportunity/dashboard"),
        api.get("/opportunity/prospects"),
      ]);
      if (!catalog) setCatalog(cat.data);
      setDashboard(dash.data);
      setProspects(list.data?.prospects || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Quietly re-fetch the prospect list without flipping the page-level loading
  // state — used by the polling loop while bulk research is in flight.
  const silentRefresh = async () => {
    try {
      const list = await api.get("/opportunity/prospects");
      setProspects(list.data?.prospects || []);
    } catch {
      // ignore — next tick will retry
    }
  };

  // Count how many rows are still being drafted by Claude. The action bar
  // shows a "drafting N…" pill while > 0; the poller keeps running until 0.
  const runningCount = useMemo(
    () => prospects.filter((p) => p.bulk_research_status === "running").length,
    [prospects]
  );

  useEffect(() => {
    if (runningCount > 0 && !pollRef.current) {
      pollRef.current = setInterval(() => {
        silentRefresh();
      }, 4000);
    }
    if (runningCount === 0 && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [runningCount]);

  const bulkResearch = async () => {
    // Bulk research all non-converted prospects that don't already have a draft
    // (the backend applies the same filter unless we pass explicit ids).
    const candidates = prospects.filter(
      (p) => p.status !== "converted" && p.bulk_research_status !== "running"
    );
    if (candidates.length === 0) {
      toast("Nothing to draft — every prospect is either converted or already being researched.");
      return;
    }
    if (
      !window.confirm(
        `Queue Claude Sonnet 4.5 to draft outreach emails for ${candidates.length} prospect(s)? ` +
          "Results will appear in each prospect's detail card as they complete (~10-20s each)."
      )
    ) {
      return;
    }
    setBulkBusy(true);
    try {
      const r = await api.post("/opportunity/prospects/bulk-research", {});
      toast.success(`Drafting ${r.data?.queued ?? candidates.length} email(s) in the background.`);
      // Refresh immediately so the "running" pill renders without waiting for
      // the next poll tick.
      await silentRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return prospects.filter((p) => {
      if (filter.intent && p.intent_tier !== filter.intent) return false;
      if (filter.clinic_type && p.clinic_type !== filter.clinic_type) return false;
      if (filter.state && (p.state || "").toUpperCase() !== filter.state.toUpperCase()) return false;
      if (filter.city && (p.city || "") !== filter.city) return false;
      if (filter.status && p.status !== filter.status) return false;
      if (filter.q) {
        const q = filter.q.toLowerCase();
        const hay = `${p.clinic_name} ${p.contact_name || ""} ${p.city || ""} ${p.website || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [prospects, filter]);

  // Derive the city dropdown options from the live prospect list — keeps
  // the filter in sync with whatever the seeding/CSV-import has produced
  // without requiring a separate catalog endpoint.
  const cityOptions = useMemo(() => {
    const set = new Set();
    for (const p of prospects) {
      if (p.city) set.add(p.city);
    }
    return Array.from(set).sort();
  }, [prospects]);

  const k = dashboard?.kpis || {};

  return (
    <ToolPageShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5 text-blue-700">
          <Radar className="w-3.5 h-3.5" /> Opportunity Engine
        </span>
      }
      title="Prospect intelligence"
      intro="A cost-effective prospect intelligence layer. Identify clinics already showing public signs of cash-pay growth intent."
    >
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-5">
        <Kpi label="Total prospects" value={k.total_prospects ?? "—"} />
        <Kpi label="High intent" value={k.high_intent ?? "—"} accent="emerald" />
        <Kpi label="Medium intent" value={k.medium_intent ?? "—"} accent="amber" />
        <Kpi label="New signals (7d)" value={k.new_signals_this_week ?? "—"} />
        <Kpi label="Ready for outreach" value={k.ready_for_outreach ?? "—"} />
        <Kpi label="Follow-ups due" value={k.followups_due ?? "—"} accent="rose" />
        <Kpi label="Converted" value={k.converted ?? "—"} accent="emerald" />
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <FilterPills
            value={filter.intent}
            onChange={(v) => setFilter((f) => ({ ...f, intent: v }))}
            options={[["", "All intent"], ["high", "High"], ["medium", "Medium"], ["low", "Low"]]}
            testIdPrefix="opp-filter-intent"
          />
          <select
            value={filter.clinic_type}
            onChange={(e) => setFilter((f) => ({ ...f, clinic_type: e.target.value }))}
            data-testid="opp-filter-clinic-type"
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs"
          >
            <option value="">All types</option>
            {(catalog?.clinic_types || []).map((t) => (
              <option key={t} value={t}>{STATUS_LABEL(t)}</option>
            ))}
          </select>
          <select
            value={filter.status}
            onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
            data-testid="opp-filter-status"
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs"
          >
            <option value="">All status</option>
            {(catalog?.statuses || []).map((s) => (
              <option key={s} value={s}>{STATUS_LABEL(s)}</option>
            ))}
          </select>
          <input
            type="text"
            value={filter.state}
            onChange={(e) => setFilter((f) => ({ ...f, state: e.target.value }))}
            placeholder="State"
            data-testid="opp-filter-state"
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs w-16"
          />
          <select
            value={filter.city}
            onChange={(e) => setFilter((f) => ({ ...f, city: e.target.value }))}
            data-testid="opp-filter-city"
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs"
            disabled={cityOptions.length === 0}
          >
            <option value="">All cities ({cityOptions.length})</option>
            {cityOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={filter.q}
              onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
              placeholder="Search…"
              data-testid="opp-filter-search"
              className="rounded-lg border border-slate-200 pl-7 pr-2 py-1 text-xs w-44"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {runningCount > 0 && (
            <span
              data-testid="opp-bulk-running-pill"
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800 text-xs font-semibold px-3 py-1.5"
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Drafting {runningCount}…
            </span>
          )}
          <button
            type="button"
            onClick={bulkResearch}
            disabled={bulkBusy}
            data-testid="opp-bulk-research-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {bulkBusy ? "Queuing…" : "Bulk draft outreach"}
          </button>
          <button
            type="button"
            onClick={() => setImporting(true)}
            data-testid="opp-import-csv-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 text-xs font-semibold px-3 py-1.5 hover:bg-slate-50"
          >
            <Upload className="w-3.5 h-3.5" /> CSV import
          </button>
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="opp-create-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800"
          >
            <Plus className="w-3.5 h-3.5" /> New prospect
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="opp-prospects-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2.5">Clinic</th>
              <th className="text-left px-3 py-2.5">Type</th>
              <th className="text-left px-3 py-2.5">Location</th>
              <th className="text-left px-3 py-2.5">Score</th>
              <th className="text-left px-3 py-2.5">Top signal</th>
              <th className="text-left px-3 py-2.5">Angle</th>
              <th className="text-left px-3 py-2.5">Status</th>
              <th className="text-right px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={8} className="text-xs text-slate-500 py-8 text-center">Loading…</td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="text-xs text-slate-500 py-10 text-center">
                  No prospects match your filters.
                </td>
              </tr>
            )}
            {filtered.map((p) => (
              <tr
                key={p.id}
                data-testid={`opp-row-${p.id}`}
                className="hover:bg-slate-50 cursor-pointer"
                onClick={() => setDetail(p.id)}
              >
                <td className="px-3 py-2.5">
                  <div className="font-bold text-slate-900">{p.clinic_name}</div>
                  {p.contact_name && (
                    <div className="text-xs text-slate-500">{p.contact_name}</div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{STATUS_LABEL(p.clinic_type)}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">
                  {p.city ? `${p.city}, ${p.state || ""}` : (p.state || "—")}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 font-semibold border ${TIER_PILL[p.intent_tier]}`}
                    >
                      {p.intent_tier}
                    </span>
                    <span className="text-sm font-bold text-slate-900">{p.intent_score}</span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600 truncate max-w-[180px]">
                  {p.top_signals?.[0]?.label || "—"}
                </td>
                <td className="px-3 py-2.5 text-xs text-blue-700 font-medium truncate max-w-[200px]">
                  {p.outreach_angle || "—"}
                </td>
                <td className="px-3 py-2.5 text-xs">
                  <span className="rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 font-semibold">
                    {STATUS_LABEL(p.status)}
                  </span>
                  {p.bulk_research_status === "running" && (
                    <span
                      data-testid={`opp-row-drafting-${p.id}`}
                      className="ml-1 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-300 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold"
                    >
                      <Loader2 className="w-2.5 h-2.5 animate-spin" /> drafting
                    </span>
                  )}
                  {p.outreach_draft?.subject && p.bulk_research_status !== "running" && (
                    <span
                      data-testid={`opp-row-draft-ready-${p.id}`}
                      className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 px-1.5 py-0.5 text-[10px] font-semibold"
                      title={p.outreach_draft.subject}
                    >
                      <Mail className="w-2.5 h-2.5" /> draft
                    </span>
                  )}
                  {p.outreach_sent_log && p.outreach_sent_log.length > 0 && (
                    <span
                      data-testid={`opp-row-sent-${p.id}`}
                      className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-1.5 py-0.5 text-[10px] font-semibold"
                      title={`Sent ${p.outreach_sent_log.length} email${p.outreach_sent_log.length > 1 ? "s" : ""}`}
                    >
                      <Mail className="w-2.5 h-2.5" /> sent
                    </span>
                  )}
                  {p.last_outreach_opened_at && (
                    <span
                      data-testid={`opp-row-opened-${p.id}`}
                      className="ml-1 inline-flex items-center gap-1 rounded-full bg-blue-600 text-white px-1.5 py-0.5 text-[10px] font-semibold"
                      title={`Opened ${p.last_outreach_opened_at}`}
                    >
                      <Eye className="w-2.5 h-2.5" /> opened
                    </span>
                  )}
                  {p.last_outreach_clicked_at && (
                    <span
                      data-testid={`opp-row-clicked-${p.id}`}
                      className="ml-1 inline-flex items-center gap-1 rounded-full bg-violet-600 text-white px-1.5 py-0.5 text-[10px] font-semibold"
                      title={`Clicked ${p.last_outreach_clicked_at}`}
                    >
                      <MousePointerClick className="w-2.5 h-2.5" /> clicked
                    </span>
                  )}
                  {p.last_outreach_bounced && (
                    <span
                      data-testid={`opp-row-bounced-${p.id}`}
                      className="ml-1 inline-flex items-center gap-1 rounded-full bg-rose-600 text-white px-1.5 py-0.5 text-[10px] font-semibold"
                      title={p.last_outreach_bounce_type || "Bounced"}
                    >
                      <AlertOctagon className="w-2.5 h-2.5" /> {p.last_outreach_bounce_type === "email.complained" ? "complaint" : "bounced"}
                    </span>
                  )}
                  {p.bulk_research_status === "failed" && (
                    <span
                      data-testid={`opp-row-draft-failed-${p.id}`}
                      className="ml-1 inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-300 text-rose-800 px-1.5 py-0.5 text-[10px] font-semibold"
                    >
                      draft failed
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right text-slate-400">
                  <ArrowRight className="w-3.5 h-3.5 inline" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && catalog && (
        <ProspectDialog
          catalog={catalog}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {importing && (
        <CsvImportDialog
          onClose={() => setImporting(false)}
          onImported={() => {
            setImporting(false);
            refresh();
          }}
        />
      )}
      {detail && catalog && (
        <ProspectDetailDialog
          prospectId={detail}
          catalog={catalog}
          onClose={() => setDetail(null)}
          onChanged={() => refresh()}
        />
      )}
    </ToolPageShell>
  );
}
